import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import httpClient from '../../../../utils/httpClient';
import { ApiSuccessResponse, ApiErrorItem } from '../../../../utils/apiSchemas';
import LanguageSelector from '../../../../components/LanguageSelector/LanguageSelector';
import MainLayout from '../../../../components/Layout/MainLayout';
import { RoutesEnum } from '../../../../utils/routes';
import { useAuth, UserLevel } from '../../../../contexts/AuthContext';

interface CustomerResponseData {
  guid: string;
  fantasyName: string;
  registrationName: string;
  registrationId: string;
  responsableName: string;
  responsableEmail: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
  userAccessesCount: number;
  configurationsCount: number;
}

interface UpdateCompanyDataPayload {
  guid: string;
  fantasyName: string;
  registrationId: string;
  responsableName: string;
  responsableEmail: string;
}

interface UpdateLanguagePayload {
  cultureLocale: string;
}

const AccountSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const normalizeLanguage = (lang: string | undefined | null) => {
    const lng = (lang ?? '').trim();
    if (!lng) return 'en-US';
    if (lng.toLowerCase().startsWith('pt')) return 'pt-BR';
    return 'en-US';
  };

  const [isDirty, setIsDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  const [tabKey, setTabKey] = useState<'company' | 'language' | 'security'>('company');
  const [initialLang, setInitialLang] = useState<string>(() => normalizeLanguage(localStorage.getItem('i18nextLng') || i18n.language || 'en-US'));
  const [companyData, setCompanyData] = useState({
    fantasyName: '',
    cnpj: '',
    responsibleName: '',
    responsibleEmail: ''
  });

  const formatCNPJ = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    const p0 = digits.slice(0, 2);
    const p1 = digits.slice(2, 5);
    const p2 = digits.slice(5, 8);
    const p3 = digits.slice(8, 12);
    const p4 = digits.slice(12, 14);
    let out = p0;
    if (digits.length > 2) out += '.' + p1;
    if (digits.length > 5) out += '.' + p2;
    if (digits.length > 8) out += '/' + p3;
    if (digits.length > 12) out += '-' + p4;
    return out;
  };

  const handleChangeCompanyField = (field: keyof typeof companyData, value: string) => {
    const newValue = field === 'cnpj' ? formatCNPJ(value) : value;
    setCompanyData(prev => ({ ...prev, [field]: newValue }));
  };
  const handleChangeTab = (_: React.SyntheticEvent, value: 'company' | 'language' | 'security') => setTabKey(value);

  const canSeeCompanyTab =
    user?.userLevel === UserLevel.SUPER_ADMIN_USER || user?.userLevel === UserLevel.ADMIN_USER;

  const [apiErrors, setApiErrors] = useState<ApiErrorItem[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!user?.customerGuid || !canSeeCompanyTab) return;

      setIsLoading(true);
      setApiErrors([]);

      try {
        const response = await httpClient.get<ApiSuccessResponse<CustomerResponseData>>(`/api/v1/portal/customer/${user.customerGuid}`);
        
        if (response.data && response.data.success && response.data.data) {
          const data = response.data.data;
          setCompanyData(prev => ({
            ...prev,
            fantasyName: data.fantasyName || '',
            cnpj: data.registrationId || '',
            responsibleName: data.responsableName || '',
            responsibleEmail: data.responsableEmail || ''
          }));
        }
      } catch (error: any) {
        console.error('Error fetching customer data:', error);
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
           setApiErrors(error.response.data.errors);
        } else {
           setApiErrors([{ 
             propertyName: 'App', 
             errorMessage: t('customer_mgmt_error_connection') || 'Erro ao carregar dados.', 
             errorCode: 'APP_ERR', 
             statusCode: 500 
           }]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomerData();
  }, [user?.customerGuid, canSeeCompanyTab, t]);

  const handleSaveCompanyData = async () => {
    if (!user?.customerGuid) return;

    setIsLoading(true);
    setApiErrors([]);
    setSaveSuccess(false);

    const payload: UpdateCompanyDataPayload = {
      guid: user.customerGuid,
      fantasyName: companyData.fantasyName,
      registrationId: companyData.cnpj,
      responsableName: companyData.responsibleName,
      responsableEmail: companyData.responsibleEmail
    };

    try {
      const response = await httpClient.put<ApiSuccessResponse<boolean>>('/api/v1/portal/customer/account-settings/update-data-company-customer', payload);

      if (response.data && response.data.success) {
        setSaveSuccess(true);
        setIsDirty(false);
        // Clear success message after 5 seconds
        setTimeout(() => setSaveSuccess(false), 5000);
      }
    } catch (error: any) {
      console.error('Error saving company data:', error);
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
         setApiErrors(error.response.data.errors);
      } else {
         setApiErrors([{ 
           propertyName: 'App', 
           errorMessage: t('login_error_generic') || 'Erro ao salvar dados.', 
           errorCode: 'APP_ERR', 
           statusCode: 500 
         }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!canSeeCompanyTab && tabKey === 'company') {
      setTabKey('language');
    }
    if (canSeeCompanyTab && tabKey === 'language') {
      // keep current selection; no auto-switch to avoid confusing the user
    }
  }, [canSeeCompanyTab, tabKey]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const attemptNavigate = useCallback(
    (action: () => void) => {
      if (isDirty) {
        setPendingNav(() => action);
        setConfirmOpen(true);
      } else {
        action();
      }
    },
    [isDirty]
  );

  useEffect(() => {
    (window as any).__wgAccountSettingsGuard = (action: () => void) => {
      attemptNavigate(action);
    };
    return () => {
      if ((window as any).__wgAccountSettingsGuard) {
        delete (window as any).__wgAccountSettingsGuard;
      }
    };
  }, [attemptNavigate]);

  const handleSaveLanguage = async () => {
    setIsLoading(true);
    setApiErrors([]);
    setSaveSuccess(false);

    const payload: UpdateLanguagePayload = {
      cultureLocale: i18n.language
    };

    try {
      const response = await httpClient.put<ApiSuccessResponse<boolean>>('/api/v1/portal/customer/account-settings/update-language-for-user', payload);

      if (response.data && response.data.success) {
        setSaveSuccess(true);
        setIsDirty(false);
        setInitialLang(i18n.language);
        // Clear success message after 5 seconds
        setTimeout(() => setSaveSuccess(false), 5000);
      }
    } catch (error: any) {
      console.error('Error saving language:', error);
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
         setApiErrors(error.response.data.errors);
      } else {
         setApiErrors([{ 
           propertyName: 'App', 
           errorMessage: t('login_error_generic') || 'Erro ao salvar idioma.', 
           errorCode: 'APP_ERR', 
           statusCode: 500 
         }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = () => {
    const target = t(RoutesEnum.RESET_PASSWORD.HREF(t)) as string;
    attemptNavigate(() => navigate(target, { state: { fromAccountSettings: true } }));
  };

  const handleLeaveWithoutSaving = () => {
    if (pendingNav) {
      const go = pendingNav;
      setPendingNav(null);
      setConfirmOpen(false);
      go();
    } else {
      setConfirmOpen(false);
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            {t('account_settings_title')}
          </Typography>
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('account_settings_success_saved')}
            </Alert>
          )}
          {apiErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {apiErrors.map((e, i) => (
                <div key={i}>{e.errorMessage || e.propertyName}</div>
              ))}
            </Alert>
          )}
          <Grid container spacing={3}>
            <Grid component={'div' as React.ElementType} item xs={12} md={3}>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={tabKey}
                onChange={handleChangeTab}
                sx={{ borderRight: 1, borderColor: 'divider' }}
              >
                {canSeeCompanyTab && <Tab label={t('account_settings_tab_company_data')} value="company" />}
                <Tab label={t('account_settings_tab_language')} value="language" />
                <Tab label={t('account_settings_tab_security')} value="security" />
              </Tabs>
            </Grid>
            <Grid component={'div' as React.ElementType} item xs={12} md={9}>
              {tabKey === 'company' && canSeeCompanyTab && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t('account_settings_tab_company_data')}
                  </Typography>
                  <Paper sx={{ p: 2, maxWidth: 900 }}>
                    <Grid container spacing={2}>
                      <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                        <Box>
                          <TextField
                            required
                            fullWidth
                            label={t('company_label_fantasy_name')}
                            value={companyData.fantasyName}
                            onChange={(e) => handleChangeCompanyField('fantasyName', e.target.value)}
                            inputProps={{ maxLength: 80 }}
                            helperText={t('common_helper_max_chars', { count: 80 })}
                          />
                        </Box>
                      </Grid>

                      <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                        <Box>
                          <TextField
                            fullWidth
                            label={t('company_label_cnpj')}
                            value={companyData.cnpj}
                            onChange={(e) => handleChangeCompanyField('cnpj', e.target.value)}
                            inputProps={{ maxLength: 80 }}
                            placeholder={t('customer_mgmt_placeholder_cnpj') as string}
                            helperText={t('create_account_helper_registration_id_format', { format: t('customer_mgmt_placeholder_cnpj') })}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                        <Box>
                          <TextField
                            fullWidth
                            label={t('create_account_label_responsible_name')}
                            value={companyData.responsibleName}
                            onChange={(e) => handleChangeCompanyField('responsibleName', e.target.value)}
                            inputProps={{ maxLength: 80 }}
                            helperText={t('common_helper_max_chars', { count: 80 })}
                          />
                        </Box>
                      </Grid>
                      <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                        <Box>
                          <TextField
                            fullWidth
                            label={t('create_account_label_responsible_email')}
                            value={companyData.responsibleEmail}
                            onChange={(e) => handleChangeCompanyField('responsibleEmail', e.target.value)}
                            inputProps={{ maxLength: 80 }}
                            helperText={t('common_helper_max_chars', { count: 80 })}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 3 }}>
                      <Button variant="contained" onClick={handleSaveCompanyData} disabled={isLoading}>
                        {t('account_settings_save')}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )}
              {tabKey === 'language' && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t('account_settings_tab_language')}
                  </Typography>
                  <Paper sx={{ p: 2, maxWidth: 600 }}>
                    <Box>
                      <LanguageSelector onChanged={(lang) => setIsDirty(lang !== initialLang)} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                      <Button variant="contained" onClick={handleSaveLanguage} disabled={!isDirty || isLoading}>
                        {t('account_settings_save')}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )}
              {tabKey === 'security' && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t('account_settings_tab_security')}
                  </Typography>
                  <Paper sx={{ p: 2, maxWidth: 600 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('account_settings_reset_password')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                      {t('account_settings_reset_password_desc')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
                      <Button variant="outlined" onClick={handleResetPassword}>
                        {t('account_settings_reset_password')}
                      </Button>
                    </Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('account_settings_delete_warning_text')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Button variant="contained" color="error">
                        {t('account_settings_delete_account')}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('account_settings_title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('account_settings_unsaved_changes_message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOpen(false); setPendingNav(null); }}>
            {t('account_settings_cancel_navigation')}
          </Button>
          <Button onClick={handleLeaveWithoutSaving}>
            {t('account_settings_leave_without_saving')}
          </Button>
          <Button variant="contained" onClick={() => {
            if (tabKey === 'language') {
               handleSaveLanguage().then(() => {
                 if (pendingNav) {
                    const go = pendingNav;
                    setPendingNav(null);
                    setConfirmOpen(false);
                    go();
                 }
               });
            } else if (tabKey === 'company') {
               handleSaveCompanyData().then(() => {
                 if (pendingNav) {
                    const go = pendingNav;
                    setPendingNav(null);
                    setConfirmOpen(false);
                    go();
                 }
               });
            } else {
               // Default behavior for other tabs
               setPendingNav(null);
               setConfirmOpen(false);
            }
          }}>
            {t('account_settings_save_and_continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default AccountSettings;
